import React, { useMemo } from 'react';
import { Tooltip as MuiTooltip } from '@mui/material';
import type { TooltipProps as MuiTooltipProps } from '@mui/material/Tooltip';
import { makeStyles } from 'tss-react/mui';
import { guards } from '@shared/helpers/typeguards.helpers';
import { wrapDisabledTooltipChild } from './tooltip.helpers';

export type TooltipProps = {
  /** Tooltip title. Zero-length titles string, undefined, null and false are never displayed. */
  title?: MuiTooltipProps['title'];
  /** Optional anchor, for example the entire row containing the trigger. */
  anchorEl?: HTMLElement | null;
  /** Allows a tooltip inside a menu while background tooltips stay suppressed. */
  allowWhileMenuOpen?: boolean;
  /** Controlled open state */
  open?: MuiTooltipProps['open'];
  /** Tooltip placement relative to the child */
  placement?: MuiTooltipProps['placement'];
  /** Visual variant; 'error' applies theme error styling */
  variant?: 'default' | 'error';
  /** Shows an arrow pointing to the child */
  arrow?: MuiTooltipProps['arrow'];
  /** Popper offset: number = distance, tuple = [skid, distance] */
  offset?: number | [number, number];
  /** Prevents pointer interactions inside the tooltip */
  disableInteractive?: MuiTooltipProps['disableInteractive'];
  /** Treat the tooltip as a description instead of replacing the child's accessible name. */
  describeChild?: MuiTooltipProps['describeChild'];
  /** Optional class applied to the rendered tooltip surface. */
  tooltipClassName?: string;
  /**
   * Keeps the tooltip reachable when the child is a disabled button.
   * Wraps the child in a span sized to the trigger (`fit-content`, or 100%
   * width when the child is `fullWidth`) so the extra node does not change layout.
   */
  showWhenDisabled?: boolean;
  /** Element the tooltip is anchored to */
  children: MuiTooltipProps['children'];
};

const useStyles = makeStyles<{ variant: TooltipProps['variant'] }>()((
  theme,
  { variant },
) => {
  switch (variant) {
    case 'error':
      return {
        tooltip: { backgroundColor: theme.palette.error.main },
        arrow: { color: theme.palette.error.main },
      };
    default:
      return { tooltip: {}, arrow: {} };
  }
});

export const Tooltip: React.FC<TooltipProps> = ({
  title,
  anchorEl,
  allowWhileMenuOpen = false,
  open,
  placement,
  disableInteractive = true,
  describeChild,
  arrow,
  offset = 0,
  variant = 'default',
  showWhenDisabled = false,
  tooltipClassName,
  children: child,
}) => {
  const [skid, distance] = guards.is.number(offset) ? [0, offset] : offset;
  const memoOffset = useMemo(
    () => [skid, distance - (arrow ? 5 : 10)], // applying some default offsets
    [skid, distance, arrow],
  );
  const { classes, cx } = useStyles({ variant });
  const computedSlotProps = useMemo<MuiTooltipProps['slotProps'] | undefined>(
    () => ({
      popper: {
        ...(anchorEl && { anchorEl }),
        className: allowWhileMenuOpen ? 'menu-action-tooltip' : undefined,
        modifiers: [{ name: 'offset', options: { offset: memoOffset } }],
      },
      tooltip: { className: cx(classes.tooltip, tooltipClassName) },
      arrow: { className: classes.arrow },
    }),
    [
      memoOffset,
      classes.tooltip,
      classes.arrow,
      cx,
      tooltipClassName,
      allowWhileMenuOpen,
      anchorEl,
    ],
  );

  return (
    <MuiTooltip
      title={title}
      open={open}
      placement={placement}
      disableInteractive={disableInteractive}
      describeChild={describeChild}
      arrow={arrow}
      classes={classes}
      slotProps={computedSlotProps}
    >
      {wrapDisabledTooltipChild(child, showWhenDisabled)}
    </MuiTooltip>
  );
};
